/**
 * Handles fetching and queuing the "what's new" feature-announcement
 * walkthroughs a user hasn't seen yet in this app, and reporting how the
 * user interacts with them back to the server.
 *
 * Trigger points (see App.js):
 *   - App boot, once the session is verified.
 *   - Right after a user authenticates mid-session (login/signup), since
 *     system.booted only ever fires once, at app start.
 *
 * The server (FeatureAnnouncementController::index) only ever returns
 * features this user hasn't already been recorded as having seen - once one
 * has been shown to completion or skipped, completeFeatureAnnouncement() /
 * skipFeatureAnnouncement() tell it so it's never sent again.
 */
import { app, payload } from "modular-openscriptjs";
import { api } from "./declarations";
import { routes } from "./backendRoutes";
import { sendEvent, consoleErrorOnLocal } from "./helpers";

// Which app this build is - matches App\Enums\FeatureAnnouncements\TargetApp
// on the backend.
const TARGET_APP = "rider";

export async function checkForFeatureAnnouncements() {
    let res;

    try {
        res = await api.get(
            routes.featureAnnouncements,
            { for: TARGET_APP },
            { silent: true },
        );
    } catch (e) {
        consoleErrorOnLocal("checkForFeatureAnnouncements failed", e);
        return;
    }

    if (!res || !res.data) return;

    const featureAnnouncements = res.data.featureAnnouncements;

    if (!Array.isArray(featureAnnouncements) || featureAnnouncements.length === 0) {
        return;
    }

    sendEvent(
        app("$e").featureAnnouncements.needs.prompt,
        payload({ featureAnnouncements }),
    );
}

/**
 * The user paged through every slide and reached the end ("Got it").
 */
export async function completeFeatureAnnouncement(id) {
    try {
        await api.post(`${routes.featureAnnouncements}/${id}/complete`);
    } catch (e) {
        consoleErrorOnLocal("completeFeatureAnnouncement failed", e);
    }
}

/**
 * The user dismissed the walkthrough early. `slideId` (optional) is
 * whichever slide they were looking at when they tapped skip/close.
 */
export async function skipFeatureAnnouncement(id, slideId = null) {
    try {
        await api.post(`${routes.featureAnnouncements}/${id}/skip`, {
            feature_announcement_slide_id: slideId,
        });
    } catch (e) {
        consoleErrorOnLocal("skipFeatureAnnouncement failed", e);
    }
}

/**
 * Fired once per slide the moment it's actually displayed to the user -
 * powers the "how far did people get" funnel in the OP reports page.
 */
export async function recordSlideView(slideId) {
    if (!slideId) return;

    try {
        await api.post(`${routes.featureAnnouncements}/slides/${slideId}/views`);
    } catch (e) {
        consoleErrorOnLocal("recordSlideView failed", e);
    }
}

/**
 * Fired when the user taps a slide's call-to-action button.
 */
export async function recordSlideClick(slideId, meta = {}) {
    if (!slideId) return;

    try {
        await api.post(`${routes.featureAnnouncements}/slides/${slideId}/clicks`, meta);
    } catch (e) {
        consoleErrorOnLocal("recordSlideClick failed", e);
    }
}
